import {
	Box,
	Button,
	Flex,
	Heading,
	HStack,
	Icon,
	IconButton,
	Input,
	Table,
	Tbody,
	Td,
	Text,
	Th,
	Thead,
	Tr,
	Tag,
	TagLabel,
	TagCloseButton,
	useDisclosure,
	useToast,
	FormControl,
	FormErrorMessage,
} from '@chakra-ui/react';
import { useContext, useMemo, useState } from 'react';
import { FaPlus, FaTrash } from 'react-icons/fa';
import { Role } from '../../hooks/useValidateSession';
import { useInviteUser } from '../../hooks/useInviteUser';
import SessionContext, { isInventoryOrgSession } from '../../contexts/SessionContext';
import LocationSelector from '../LocationSelector';
import { ImportUploadModal } from '../import/ImportUploadModal';
import { getInvitePositionOptions } from '../../utils/invitePositionOptions';
import {
	INVITE_TEMPLATE_ACCEPT,
	downloadInviteTemplate,
	isSupportedInviteFile,
	parseInviteWorkbook,
} from '../../utils/inviteUsersTemplate';
import { StepFooter } from './StepFooter';
import PopoverSelector from '../../pages/reporting/components/PopoverSelector';
import { isValidInviteEmail } from '../../utils/isValidInviteEmail';

interface InviteUsersStepProps {
	organizationId: string;
	locations: Array<{ _id: string; title: string; }>;
	onComplete: () => void;
	onSkip: () => void;
	onBack?: () => void;
	/** When true (invoicing-only org), position is locked to Invoicing for every invite row. */
	isInvoicingOrg?: boolean;
}

interface InviteRow {
	id: string;
	email: string;
	position: Role | '';
	locationId: Array<string> | 'all';
	emailTouched?: boolean;
}

let rowCounter = 0;
const nextId = () => `invite-${++rowCounter}`;

const defaultInvitePosition = (isInvoicingOrg: boolean): Role | '' =>
	isInvoicingOrg ? 'invoicing' : '';

const newInviteRow = (isInvoicingOrg: boolean): InviteRow => ({
	id: nextId(),
	email: '',
	position: defaultInvitePosition(isInvoicingOrg),
	locationId: 'all',
});

export const InviteUsersStep = ({
	organizationId,
	locations,
	onComplete,
	onSkip,
	onBack,
	isInvoicingOrg = false,
}: InviteUsersStepProps) => {
	const toast = useToast();
	const inviteUser = useInviteUser();
	const sessionContext = useContext(SessionContext);
	const orgInventoryEnabled =
		sessionContext?.isAuthenticated === true && sessionContext.inventoryEnabled === true;
	const orgInvoicingEnabled =
		sessionContext?.isAuthenticated === true && sessionContext.permissions.includes('invoicing');
	const isInventoryOrg = isInventoryOrgSession(sessionContext);

	const positionOptions = useMemo(
		() =>
			isInvoicingOrg
				? [{ value: 'invoicing' as Role, label: 'Invoicing' }]
				: getInvitePositionOptions({
					includeTechnician: orgInventoryEnabled,
					includeInvoicing: orgInvoicingEnabled,
					inventoryOrg: isInventoryOrg,
				}),
		[isInvoicingOrg, isInventoryOrg, orgInventoryEnabled, orgInvoicingEnabled],
	);
	const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);
	const uploadModal = useDisclosure();

	const [rows, setRows] = useState<InviteRow[]>([newInviteRow(isInvoicingOrg)]);
	const [isSending, setIsSending] = useState(false);

	const addRow = () => {
		setRows((prev) => [...prev, newInviteRow(isInvoicingOrg)]);
	};

	const removeRow = (id: string) => {
		setRows((prev) => prev.filter((r) => r.id !== id));
	};

	const updateRow = (id: string, field: 'email' | 'position', value: string) => {
		setRows((prev) =>
			prev.map((r) => {
				if (r.id !== id) return r;
				if (field === 'email') {
					return {
						...r,
						email: value,
						emailTouched: value.trim() === '' ? false : r.emailTouched,
					};
				}
				return { ...r, position: value as Role | '' };
			}),
		);
	};

	const markEmailTouched = (id: string) => {
		setRows((prev) => prev.map((r) => (r.id === id ? { ...r, emailTouched: true } : r)));
	};

	const updateRowLocation = (id: string, value: Array<string> | 'all') => {
		setRows((prev) => prev.map((r) => (r.id === id ? { ...r, locationId: value } : r)));
	};

	const handleDownloadTemplate = async () => {
		await downloadInviteTemplate(locations, positionOptions);
	};

	const handleUploadFile = async (file: File) => {
		try {
			const { rows: parsed, skippedSheets } = await parseInviteWorkbook(file, {
				locations,
				positionOptions,
				isInvoicingOrg,
			});

			if (parsed.length === 0) {
				toast({
					title: 'No valid rows',
					description: 'No rows with matching locations and emails were found.',
					status: 'error',
					duration: 4000,
					isClosable: true,
					position: 'bottom-right',
				});
				return;
			}

			setRows(parsed.map((row) => ({ id: nextId(), ...row })));
			setUploadedFileName(file.name);

			if (skippedSheets > 0) {
				toast({
					title: `${parsed.length} user(s) imported`,
					description: `${skippedSheets} sheet(s) with unmatched locations were skipped.`,
					status: 'warning',
					duration: 4000,
					isClosable: true,
					position: 'bottom-right',
				});
			} else {
				toast({
					title: 'File uploaded',
					description: `${parsed.length} user(s) ready to invite.`,
					status: 'success',
					duration: 3000,
					isClosable: true,
					position: 'bottom-right',
				});
			}
		} catch {
			toast({
				title: 'Failed to read file',
				description: 'The file could not be parsed.',
				status: 'error',
				duration: 4000,
				isClosable: true,
				position: 'bottom-right',
			});
		}
	};

	const handleRemoveUpload = () => {
		setRows([newInviteRow(isInvoicingOrg)]);
		setUploadedFileName(null);
	};

	const filledRows = rows.filter((r) => r.email.trim());
	const canSendInvitations =
		filledRows.length > 0 &&
		filledRows.every(
			(r) =>
				isValidInviteEmail(r.email) &&
				(isInvoicingOrg || r.position) &&
				(r.locationId === 'all' || r.locationId.length > 0),
		);

	const handleSendInvitations = async () => {
		if (!canSendInvitations) return;

		const deduplicated = new Map<
			string,
			{ email: string; position: Role; locationId: Array<string> | 'all'; }
		>();
		for (const row of filledRows) {
			const locationKey =
				row.locationId === 'all' ? 'all' : [...row.locationId].sort().join(',');
			const key = `${row.email.trim().toLowerCase()}::${locationKey}`;
			if (!deduplicated.has(key)) {
				deduplicated.set(key, {
					email: row.email.trim(),
					position: (isInvoicingOrg ? 'invoicing' : row.position) as Role,
					locationId: row.locationId,
				});
			}
		}

		setIsSending(true);
		let succeeded = 0;
		let failed = 0;

		const invites = Array.from(deduplicated.values());
		for (const invite of invites) {
			try {
				await inviteUser.mutateAsync(invite);
				succeeded++;
			} catch {
				failed++;
			}
		}

		setIsSending(false);

		if (failed === 0) {
			toast({
				title: `${succeeded} invitation(s) sent`,
				status: 'success',
				duration: 3000,
				isClosable: true,
				position: 'bottom-right',
			});
			onComplete();
		} else {
			toast({
				title: `${succeeded} sent, ${failed} failed`,
				description: 'Some invitations could not be sent. Check emails and try again.',
				status: 'warning',
				duration: 5000,
				isClosable: true,
				position: 'bottom-right',
			});
		}
	};

	return (
		<Flex direction="column" flex={1} width="100%" minH={0} overflow="hidden">
			<Box pt={4} flexShrink={0}>
				<Flex justify="space-between" align="center" mb={1} pr={8}>
					<Heading size="md">Invite Users</Heading>
					<Button size="sm" variant="outline" onClick={uploadModal.onOpen}>
						Upload Template
					</Button>
				</Flex>
				<Text color="gray.500" fontSize="sm" mb={4}>
					Invite team members to your organization. Add users manually or download the Excel
					template with per-location sheets and position dropdowns. You can always invite more
					users later from Settings.
				</Text>
				{uploadedFileName && (
					<HStack mb={3}>
						<Tag size="sm" colorScheme="green" borderRadius="full" variant="subtle">
							<TagLabel fontSize="xs">{uploadedFileName}</TagLabel>
							<TagCloseButton onClick={handleRemoveUpload} />
						</Tag>
					</HStack>
				)}
			</Box>

			<Box flex={1} minH={0} overflowY="auto">
				<Box borderWidth={1} borderRadius="md" overflow="hidden" mb={3}>
					<Table size="sm">
						<Thead bg="gray.50">
							<Tr>
								<Th position="sticky" top={0} bg="gray.50" zIndex={1} py={2.5}>
									Email
								</Th>
								<Th position="sticky" top={0} bg="gray.50" zIndex={1} py={2.5}>
									Position
								</Th>
								<Th position="sticky" top={0} bg="gray.50" zIndex={1} py={2.5}>
									Location
								</Th>
								<Th position="sticky" top={0} bg="gray.50" zIndex={1} py={2.5} width="44px" />
							</Tr>
						</Thead>
						<Tbody>
							{rows.map((row) => {
								const showEmailError =
									!!row.emailTouched && row.email.trim() !== '' && !isValidInviteEmail(row.email);
								return (
									<Tr key={row.id}>
										<Td>
											<FormControl isInvalid={showEmailError}>
												<Input
													size="sm"
													type="email"
													value={row.email}
													onChange={(e) => updateRow(row.id, 'email', e.target.value)}
													onBlur={() => markEmailTouched(row.id)}
													placeholder="user@example.com"
												/>
												{showEmailError && (
													<FormErrorMessage fontSize="xs">Please enter a valid email address</FormErrorMessage>
												)}
											</FormControl>
										</Td>
										<Td>
											<PopoverSelector
												size="md"
												width="100%"
												options={positionOptions}
												value={isInvoicingOrg ? 'invoicing' : row.position}
												placeholder="Select position"
												isDisabled={isInvoicingOrg}
												onChange={(nextValue) =>
													updateRow(row.id, 'position', nextValue)
												}
											/>
										</Td>
										<Td>
											<LocationSelector
												selectedLocations={row.locationId}
												onLocationChange={(value) => updateRowLocation(row.id, value)}
												width="100%"
											/>
										</Td>
										<Td>
											<IconButton
												aria-label="Remove row"
												icon={<Icon as={FaTrash as React.ElementType} boxSize={3} />}
												size="xs"
												variant="ghost"
												colorScheme="red"
												onClick={() => removeRow(row.id)}
												isDisabled={rows.length <= 1}
											/>
										</Td>
									</Tr>
								);
							})}
						</Tbody>
					</Table>
				</Box>

				<Button
					size="sm"
					variant="ghost"
					leftIcon={<Icon as={FaPlus as React.ElementType} boxSize={3} />}
					onClick={addRow}
					color="gray.600"
				>
					Add another user
				</Button>
			</Box>

			<StepFooter
				back={onBack ? { onClick: onBack } : undefined}
				secondaryActions={[{ label: 'Skip', onClick: onSkip, variant: 'outline' }]}
				primary={{
					label: 'Send Invitations',
					onClick: () => void handleSendInvitations(),
					disabled: !canSendInvitations,
					isLoading: isSending,
					loadingText: 'Sending...',
				}}
			/>

			<ImportUploadModal
				isOpen={uploadModal.isOpen}
				onClose={uploadModal.onClose}
				onFileSelected={(file) => {
					void handleUploadFile(file);
					uploadModal.onClose();
				}}
				onDownloadTemplate={() => void handleDownloadTemplate()}
				title="Upload File"
				accept={INVITE_TEMPLATE_ACCEPT}
				supportedFormatsLabel="CSV, XLS, XLSX"
				maxSizeMB={25}
				isSupportedFile={isSupportedInviteFile}
			/>
		</Flex>
	);
};
